import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminMetrics() {
  const { isAdmin } = useApp();
  const { t } = useI18n();
  const [metrics, setMetrics] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadMetrics();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (autoRefresh && isAdmin) {
      const interval = setInterval(loadMetrics, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, isAdmin]);

  async function loadMetrics() {
    try {
      const [metricsRes, healthRes] = await Promise.all([
        api.get('/admin/metrics'),
        api.get('/admin/metrics/health-detailed').catch(() => null),
      ]);
      setMetrics(metricsRes.data);
      if (healthRes) setHealth(healthRes.data);
    } catch (e) {
      console.error('Metrics load error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function resetMetrics() {
    if (!confirm('Сбросить все метрики?')) return;
    await api.post('/admin/metrics/reset');
    loadMetrics();
  }

  if (!isAdmin) {
    return <div className="page"><BackButton /><h2>⛔ Доступ запрещён</h2></div>;
  }

  const StatCard = ({ icon, label, value, sub, color }) => (
    <div style={{
      background: `rgba(${color || '108,92,231'},0.15)`,
      borderRadius: 12,
      padding: 16,
      textAlign: 'center',
      flex: 1,
      minWidth: 80,
    }}>
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, opacity: 0.5 }}>{sub}</div>}
    </div>
  );

  return (
    <div className="page"><BackButton />
      <h1>📊 Performance Metrics</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={loadMetrics}>🔄 Refresh</button>
        <button 
          className={`btn ${autoRefresh ? 'btn-success' : 'btn-secondary'}`}
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          {autoRefresh ? '⏸ Stop' : '▶️ Auto (5s)'}
        </button>
        <button className="btn btn-danger" onClick={resetMetrics}>🗑 Reset</button>
      </div>

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : (
        <>
          {/* System Health */}
          {health?.system && (
            <div style={{ marginBottom: 20 }}>
              <h3>💻 System Health</h3>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <StatCard 
                  icon="🖥" 
                  label="CPU" 
                  value={`${health.system.cpu_percent}%`}
                  color={health.system.cpu_percent > 80 ? '255,107,107' : '0,206,201'}
                />
                <StatCard 
                  icon="💾" 
                  label="Memory" 
                  value={`${health.system.memory_percent}%`}
                  color={health.system.memory_percent > 80 ? '255,107,107' : '0,206,201'}
                />
                <StatCard 
                  icon="💿" 
                  label="Disk" 
                  value={`${health.system.disk_percent}%`}
                  color={health.system.disk_percent > 90 ? '255,107,107' : '0,206,201'}
                />
                {health.process && (
                  <StatCard 
                    icon="🔧" 
                    label="Process RAM" 
                    value={`${health.process.memory_mb}MB`}
                  />
                )}
              </div>
            </div>
          )}

          {/* Request Metrics */}
          {metrics && (
            <>
              <div style={{ marginBottom: 20 }}>
                <h3>📈 Request Metrics</h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <StatCard icon="📊" label="Total Requests" value={metrics.requests_total} />
                  <StatCard icon="⚡" label="Avg Latency" value={`${metrics.avg_latency_ms}ms`} />
                  <StatCard 
                    icon="🐢" 
                    label="Slow Requests" 
                    value={metrics.slow_requests_count}
                    color={metrics.slow_requests_count > 10 ? '255,107,107' : '0,206,201'}
                  />
                </div>
              </div>

              {/* Status Codes */}
              <div style={{ marginBottom: 20 }}>
                <h3>📋 By Status Code</h3>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(metrics.requests_by_status || {}).map(([code, count]) => (
                    <StatCard 
                      key={code}
                      icon={code.startsWith('2') ? '✅' : code.startsWith('4') ? '⚠️' : '❌'}
                      label={`HTTP ${code}`}
                      value={count}
                      color={code.startsWith('2') ? '0,206,201' : code.startsWith('5') ? '255,107,107' : '255,193,7'}
                    />
                  ))}
                </div>
              </div>

              {/* Top Paths */}
              <div style={{ marginBottom: 20 }}>
                <h3>🔥 Top Endpoints</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(metrics.requests_by_path || {}).slice(0, 10).map(([path, count]) => (
                    <div key={path} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: 8,
                    }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{path}</span>
                      <span style={{ fontWeight: 600 }}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slow Requests */}
              {metrics.recent_slow_requests?.length > 0 && (
                <div>
                  <h3>🐢 Recent Slow Requests</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {metrics.recent_slow_requests.map((req, i) => (
                      <div key={i} style={{
                        padding: 12,
                        background: 'rgba(255,107,107,0.15)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}>
                        <div>
                          <span style={{ fontWeight: 600 }}>{req.method}</span> {req.path}
                        </div>
                        <div style={{ color: '#ff6b6b' }}>
                          {req.latency_ms}ms • Status {req.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      <style>{`
        .btn-success { background: #00cec9; }
        .btn-danger { background: #ff6b6b; }
      `}</style>
    </div>
  );
}
